<template>
    <div v-if="show">
        <div class="card">
            <div class="card-header card-header-flex pb-2">
                <div class="w-100 mt-2">
                    <div class="row">
                        <div class="col-7">
                            <h5 class="mt-3 ml-0 mr-3 mb-2">
                                <strong>
                                    <template v-if="operation === null">
                                        {{$t('title.systemMessages')}}
                                        <span v-if="pagination.total">({{pagination.total}})</span>
                                    </template>
                                    <template v-else>{{ $t(operationTitle) }}</template>
                                </strong>
                            </h5>
                        </div>
                        <div class="col-5 text-right">
                            <div v-if="operation === null">
                                <div class="mt-3">
                                    <!-- Using components -->
                                    <b-input-group class="mt-3">
                                        <b-form-input type="search" class="form-control form-control-sm"
                                                      :placeholder="$t('input.whatAreYouLookingFor')"
                                                      v-on:keyup.enter="handleSearch" v-model="search"></b-form-input>
                                        <b-input-group-append>
                                            <b-button @click="setOperation('add')" variant="info" size="sm"
                                                      v-if="$global.hasPermission('systemmessagesstore')"
                                                      :title="$t('button.title.addNewItem')"
                                                      v-b-tooltip.hover>
                                                <i class="fe fe-plus"></i> {{$t('button.addNew')}}
                                            </b-button>
                                            <b-button size="sm" :title="$t('button.title.filterRecords')"
                                                      variant="outline-info"
                                                      @click="filters.visible = !filters.visible" v-b-tooltip.hover
                                                      v-if="$global.hasAnyPermission(['systemmessagesview', 'systemmessagesownview'])">
                                                <i class="fa fa-filter"></i>
                                            </b-button>
                                            <b-button size="sm" :title="$t('button.title.resetList')"
                                                      variant="outline-info"
                                                      @click="handleResetFilterClick()" v-b-tooltip.hover>
                                                <i class="fa fa-refresh"></i>
                                            </b-button>
                                        </b-input-group-append>
                                    </b-input-group>
                                </div>
                            </div>
                            <div v-else>
                                <b-button variant="warning" size="sm" class="mt-3"
                                          @click="handleOperationClose()"
                                          v-b-tooltip.hover :title="$t('button.title.cancel')">
                                    <i class="fa fa-arrow-left"></i> {{$t('button.cancel')}}
                                </b-button>
                            </div>
                        </div>
                    </div><!-- /.row -->
                </div><!-- /.w-100 -->
            </div><!-- /.card-header -->
            <div class="card-body">
                <div class="systemmessages-table">
                    <b-table hover responsive
                             ref="table"
                             :busy="listingLoading"
                             :items="dataSource"
                             :fields="columns"
                             :sort-by.sync="sortField"
                             @sort-changed="handleSortChange">
                        <template v-slot:cell(type)="record">
                            <span class="text-capitalize">{{record.item.type}}</span>
                        </template>
                        <template v-slot:cell(title)="record">
                            <a-tag v-for="(t, index) in record.item.roles" :key="index"
                                   v-if="record.item.type == 'role'"
                                   color="blue">
                                {{t.name}}
                            </a-tag>
                            <a-tag v-for="(t, index) in record.item.users" :key="index"
                                   v-if="record.item.type == 'user'"
                                   color="blue">
                                {{t.name}}
                            </a-tag>
                        </template>
                        <template v-slot:cell(start_date)="record">
                            {{$global.utcDateToLocalDate(record.item.start_date) }}
                        </template>
                        <template v-slot:cell(action)="record">
                            <a @click="setOperation('edit', record.item.id)"
                               :title="$t('button.title.editItem')"
                               v-if="$global.hasPermission('systemmessagesupdate') && handleEditOperation(record.item.created_at)"
                               v-b-tooltip.hover>
                                <i class="fe fe-edit"></i>
                            </a>
                            <a-popconfirm title="Are you sure？" @confirm="handleDeleteClick(record.item.id)"
                                          v-if="$global.hasPermission('systemmessagesdestroy')">
                                <i slot="icon" class="fe fe-trash"></i>
                                <a class=" ml-1"
                                   :title="$t('button.title.deleteItem')"
                                   v-b-tooltip.hover>
                                    <i class="fe fe-trash"></i>
                                </a>
                            </a-popconfirm>
                        </template>
                    </b-table><!-- /.b-table -->

                    <div class="clearfix">
                        <div class="float-left ">
                            <b-form-select v-model="pagination.perPage" :options="[5, 10, 50, 100, 1000]"
                                           size="sm"></b-form-select>
                        </div>
                        <div class="float-right">
                            <b-pagination
                                v-model="pagination.current"
                                :total-rows="pagination.total"
                                :per-page="pagination.perPage"
                                :first-text="$t('paginations.first')"
                                :prev-text="$t('paginations.prev')"
                                :next-text="$t('paginations.next')"
                                :last-text="$t('paginations.last')"
                            ></b-pagination>
                        </div><!-- /.pull-right -->
                    </div><!-- /.clearfix -->
                </div><!-- /.systemmessages-table -->
                <div class="systemmessages-operation">
                    <a-drawer
                        placement="right"
                        :width="'650px'"
                        :wrapStyle="{overflow: 'auto',paddingBottom: '108px'}"
                        :closable="false"
                        @close="handleOperationClose"
                        :visible="operation !== null && operation !== 'detail'"
                        :zIndex="10"
                        :title="$t(operationTitle)"
                    >
                        <form @submit.prevent="handleSubmit" autocomplete="off">
                            <b-row>
                                <b-col cols="12">
                                    <b-row>
                                        <b-col sm="3">
                                            <b-form-group
                                                :label="$t('input.variantType')+' *'"
                                                label-for="variant_type"
                                                :invalid-feedback="formErrors.first('variant_type')"
                                            >
                                                <b-form-radio
                                                    v-model="formFields.variant_type"
                                                    value="info">
                                                    {{$t('title.info')}}
                                                </b-form-radio>
                                                <b-form-radio
                                                    v-model="formFields.variant_type"
                                                    value="warning">
                                                    {{$t('title.warning')}}
                                                </b-form-radio>
                                                <b-form-radio
                                                    v-model="formFields.variant_type"
                                                    value="danger">
                                                    {{$t('title.danger')}}
                                                </b-form-radio>
                                                <b-form-radio
                                                    v-model="formFields.variant_type"
                                                    value="success">
                                                    {{$t('title.success')}}
                                                </b-form-radio>
                                            </b-form-group>
                                        </b-col><!--/b-col-->
                                        <b-col sm="6">
                                            <b-form-group
                                                :label="$t('input.type')+' *'"
                                                label-for="type"
                                                :invalid-feedback="formErrors.first('type')"
                                            >
                                                <b-form-radio :disabled="formFields.id ? true : false"
                                                              v-model="formFields.type"
                                                              value="role">
                                                    {{$t('title.role')}}
                                                </b-form-radio>
                                                <b-form-radio :disabled="formFields.id ? true : false"
                                                              v-model="formFields.type"
                                                              value="user">
                                                    {{$t('title.user')}}
                                                </b-form-radio>
                                            </b-form-group>
                                        </b-col><!--/b-col-->
                                        <b-col sm="6">
                                            <b-form-group
                                                :label="$t('input.select') +' '+ (formFields.type == 'role' ? $t('title.role') : $t('title.user')) + ' *'"
                                                label-for="object_id"
                                                :invalid-feedback="formErrors.first('role_id') || formErrors.first('user_id')"
                                            >
                                                <treeselect
                                                    v-if="formFields.type == 'role'"
                                                    :multiple="true"
                                                    :options="dropdowns.roles"
                                                    placeholder=""
                                                    v-model="formFields.role_id"
                                                    :class="[{'invalid is-invalid': (formErrors.has('role_id'))}]"
                                                />
                                                <treeselect
                                                    v-else
                                                    :multiple="true"
                                                    :options="dropdowns.users"
                                                    placeholder=""
                                                    v-model="formFields.user_id"
                                                    :class="[{'invalid is-invalid': (formErrors.has('user_id'))}]"
                                                />
                                            </b-form-group>
                                        </b-col><!--/b-col-->
                                    </b-row><!--/b-row-->
                                    <b-row>
                                        <b-col sm="6">
                                            <b-form-group
                                                :label="$t('input.startDate')"
                                                label-for="start_date"
                                                :class="{'has-error': formErrors.has('start_date')}">
                                                <a-date-picker
                                                    :show-time="{ format: 'HH:mm' }"
                                                    placeholder=""
                                                    format="DD-MM-YYYY hh:mm A"
                                                    v-model="formFields.start_date"
                                                />
                                                <div class="invalid-feedback">
                                                    {{formErrors.first('start_date')}}
                                                </div>
                                            </b-form-group>
                                        </b-col><!--/b-col-->
                                    </b-row>
                                    <b-row>
                                        <b-col sm="12">
                                            <b-form-group
                                                :label="$t('input.description') + ' *'"
                                                label-for="description"
                                                :invalid-feedback="formErrors.first('description')">
                                                <b-form-textarea
                                                    id="description"
                                                    v-model="formFields.description"
                                                    type="text"
                                                    :state="((formErrors.has('description') ? false : null))"
                                                ></b-form-textarea>
                                            </b-form-group>
                                        </b-col><!--/b-col-->
                                    </b-row>
                                </b-col><!--/b-col-->
                                <div class="drawer-footer">
                                    <b-button
                                        size="sm"
                                        type="submit"
                                        variant="primary"
                                        :disabled="global.pendingRequests > 0"
                                        v-b-tooltip.hover :title="$t('button.title.save')"
                                    >
                                        <clip-loader style="display: inline" :loading="true" color="#fff"
                                                     size="12px"
                                                     v-if="global.pendingRequests > 0"></clip-loader>
                                        <i class="fa fa-save mr-1"></i>
                                        {{$t('button.save')}}
                                    </b-button>
                                    <b-button variant="warning" class="ml-3"
                                              size="sm" @click="handleOperationClose()"
                                              v-b-tooltip.hover :title="$t('button.title.cancel')">
                                        <i class="fa fa-arrow-left mr-1"></i> {{$t('button.cancel')}}
                                    </b-button>
                                </div>
                            </b-row><!--/b-row-->
                        </form><!--/form-->
                    </a-drawer>
                </div><!--/.systemmessages-operation-->
                <div class="filter-container">
                    <a-drawer
                        placement="left"
                        :width="'360px'"
                        :wrapStyle="{overflow: 'auto',paddingBottom: '108px'}"
                        :closable="false"
                        @close="filters.visible = !filters.visible"
                        :visible="!operation && filters.visible"
                        :zIndex="10"
                        :title="$t('title.advanceFilters')"
                    >
                        <form @submit.prevent="loadList(listQueryParams, true)" autocomplete="off">
                            <b-row>
                                <b-col sm="12">
                                    <b-form-group
                                        :label="$t('input.fromAddedDate')"
                                        label-for="fromDate">
                                        <b-form-datepicker placeholder="" id="fromDate" v-model="filters.from_date"
                                                           class="mb-2"></b-form-datepicker>
                                    </b-form-group>
                                </b-col><!--/b-col-->
                                <b-col sm="12">
                                    <b-form-group
                                        :label="$t('input.toAddedDate')"
                                        label-for="toDate">
                                        <b-form-datepicker placeholder="" id="toDate" v-model="filters.to_date"
                                                           class="mb-2"></b-form-datepicker>
                                    </b-form-group>
                                </b-col><!--/b-col-->
                            </b-row>
                            <div class="drawer-footer">
                                <b-button size='sm' variant="info" @click="filters.visible = !filters.visible"
                                          class="mr-2" :title="$t('button.title.closePanel')" v-b-tooltip.hover>
                                    {{$t('button.close')}}
                                </b-button>
                                <b-button size='sm' variant="warning" @click="handleResetFilterClick" class="mr-2"
                                          :title="$t('button.title.resetFilter')" v-b-tooltip.hover>
                                    {{$t('button.reset')}}
                                </b-button>
                                <b-button size='sm' variant="primary" button="submit" type="filled"
                                          :title="$t('button.title.filterRecords')" v-b-tooltip.hover>
                                    {{$t('button.apply')}}
                                </b-button>
                            </div><!-- /.drawer-footer -->
                        </form>
                    </a-drawer>
                </div><!-- /.filter-container -->
            </div><!-- /.card-body-->
        </div><!-- /.card -->
    </div>
</template>
<script>
    import ListingMixin from '../../util/ListingMixin'
    import Error from '../../util/Error'
    import {mapState} from 'vuex'
    import Datepicker from 'vuejs-datepicker'
    import {request} from '../../util/Request'
    import Treeselect from '@riophae/vue-treeselect'
    import moment from 'moment-timezone'

    const FORM_STATE = {
        type: 'role',
        description: null,
        role_id: [],
        user_id: [],
        start_date: null,
        variant_type: "info",
        _method: 'post',
    };

    const FILTER_STATE = {
        visible: false,
        from_date: null,
        to_date: null,
    };

    const COLUMN_DEFINITION = (self) => [
        {
            label: self.$t('column.type'),
            key: 'type',
            sortable: true,
            sortKey: 'type',
        },
        {
            label: self.$t('column.title'),
            key: 'title',
            sortable: false,
        },
        {
            label: self.$t('column.startDate'),
            key: 'start_date',
            sortable: false,
        },
        (self.$global.hasAnyPermission(['systemmessagesupdate', 'systemmessagesdestroy'])
            ? {
                label: self.$t('column.action'),
                class: 'text-right',
                key: 'action',
                width: 150,
            } : {}),
    ];

    export default {
        mixins: [ListingMixin],
        components: {
            Datepicker,
            Treeselect
        },
        data() {
            return {
                operationTitle: 'title.systemMessages',
                formFields: {...FORM_STATE},
                filters: {...FILTER_STATE},
                listUrl: 'system/messages',
                columns: COLUMN_DEFINITION(this),
                dropdowns: {
                    roles: [],
                    users: [],
                },
                show: true,
            }
        },
        mounted() {
            if (this.$route.query && this.$route.query.operation && this.$route.query.oToken) {
                this.handleEditClick(this.$route.query.oToken)
            }

            this.$title = this.$t('topBar.navigations.modules.systemMessages')
            this.getRoles()
            this.getUsers()
        },
        methods: {
            setOperation(operation = 'add', operationToken = null) {
                this.operationTitle = (operation === 'add' ? 'title.addSystemMessages' : 'title.editSystemMessages')
                this.$router.replace({
                    query: Object.assign({},
                        this.$route.query,
                        {
                            ...this.listQueryParams,
                            operation: operation,
                            oToken: operationToken,
                        },
                    ),
                }).then(() => {
                }).catch(() => {
                })
            },
            async handleSubmit() {
                this.formErrors = new Error({})
                try {
                    const {start_date} = this.formFields
                    const response = await request({
                        url: this.formFields.id ? 'system/messages/update' : 'system/messages/create',
                        method: 'post',
                        data: {
                            ...this.formFields,
                            start_date: ((start_date) ? this.$global.dateToUtcDate(moment(start_date).format('YYYY-MM-DD HH:mm:ss'), 'YYYY-MM-DD hh:mm A', 'YYYY-MM-DD HH:mm:ss') : null),
                        },
                    })

                    if (this.formFields.id) {
                        this.itemUpdated()
                    } else {
                        this.itemAdded()
                    }

                    this.handleOperationClose()
                } catch (error) {
                    if (error.request && error.request.status && error.request.status === 422) {
                        this.formErrors = new Error(JSON.parse(error.request.responseText).errors)
                        return false
                    }

                    this.handleServerError(error)
                }
            },
            async handleDeleteClick(id) {
                try {
                    const response = await request({
                        method: 'post',
                        url: '/system/messages/delete',
                        data: {
                            id: id,
                        },
                    })
                    this.loadList(this.listQueryParams)
                    this.itemDeleted()
                } catch (errors) {
                    this.itemDeleteFails()
                }
            },
            async handleEditClick(id) {
                try {
                    const response = await request({
                        method: 'get',
                        url: `/system/messages/detail/${id}`,
                    })
                    this.operationTitle = this.$t('title.editSystemMessages')
                    const {data} = response
                    this.formFields = {
                        ...data,
                        user_id: data.users ? data.users.map((i) => i.id) : [],
                        role_id: data.roles ? data.roles.map((i) => i.id) : []
                    }
                } catch (e) {
                    this.itemEditFails()
                    this.formFields = {...FORM_STATE}
                }
            },
            hasListAccess() {
                return this.$global.hasAnyPermission(['systemmessagesview', 'systemmessagesownview'])
            },
            getExtraParams() {
                return {
                    filters: {
                        ...this.filters,
                        from_date: ((this.filters.from_date) ? this.$global.dateToUtcDate(this.filters.from_date, 'YYYY-MM-DD', 'YYYY-MM-DD') : ''),
                        to_date: ((this.filters.to_date) ? this.$global.dateToUtcDate(this.filters.to_date, 'YYYY-MM-DD', 'YYYY-MM-DD') : ''),
                    },
                }
            },
            handleResetFilterClick() {
                this.filters = {...FILTER_STATE}
                this.isFilterApplied = 'reset'
                this.handleResetClick()
                this.loadList(this.listQueryParams)
            },
            afterCloseOperation() {
                this.formFields = {...FORM_STATE}
            },
            async getRoles() {
                try {
                    const response = await request({
                        url: `/dropdowns/roles`,
                        method: 'post',
                    })

                    this.dropdowns.roles = response.data.map((item) => {
                        return {
                            id: item.id,
                            label: item.name,
                        }
                    })
                } catch (e) {

                }
            },
            async getUsers() {
                try {
                    const response = await request({
                        url: `/dropdowns/users`,
                        method: 'post',
                    })

                    this.dropdowns.users = response.data.map((item) => {
                        return {
                            id: item.guid,
                            label: item.name,
                        }
                    })
                } catch (e) {

                }
            },
            handleEditOperation(date) {
                return moment().toDate() < moment(date).add(3, 'hours')
            }
        },
        computed: {
            ...mapState([
                'global',
                'settings'
            ]),
        },
    }
</script>

